/**
 * Feuilles de brouillon — porte de validation, en fonctions PURES (aucun client
 * Supabase) : utilisées par l'éditeur (`scratch-admin.ts`) et par le script de
 * génération en lot (`apps/app/scripts/generate-scratch-sheets.ts`), pour que
 * les deux enregistrent exactement la même chose.
 */

import { modelEngineVersion, validateModel } from '@jeraisonne/reasoner';
import type { ExerciseBlock } from '@/lib/exercise-v2/types';

export interface ScratchIssue {
  code: string;
  message: string;
  line?: string;
}

// ── questions et QCM, dans l'ordre de l'edge fn ────────────────────────────

const byPosition = (a: ExerciseBlock, b: ExerciseBlock) => a.position - b.position;
const str = (v: unknown) => (typeof v === 'string' ? v : '');

/** Questions de l'exercice, dans l'ordre (index 0-based de l'edge fn). */
export function orderedQuestionBlocks(blocks: ExerciseBlock[]): ExerciseBlock[] {
  return blocks
    .filter((b) => b.section === 'statement' && b.block_type === 'statement_question')
    .sort(byPosition);
}

export interface QuestionQcm {
  /** bonne réponse : la DERNIÈRE correction qui a une réponse (comme l'edge fn) */
  correctId: string | null;
  /**
   * toutes les bonnes réponses : la correction, puis les distracteurs marqués justes
   * (`is_correct`) d'un QCM à plusieurs bonnes réponses (lot 6.6) ; absent = la
   * correction seule
   */
  correctIds?: string[];
  /** tous les choix : bonne réponse puis distracteurs */
  ids: string[];
  /** QCM à plusieurs bonnes réponses : le modèle les marque toutes justes */
  multiple: boolean;
}

export function questionQcm(blocks: ExerciseBlock[], questionId: string): QuestionQcm {
  const corrections = blocks
    .filter(
      (b) =>
        b.section === 'correction' && b.block_type === 'correction' && b.parent_id === questionId,
    )
    .sort(byPosition)
    .filter((c) => str(c.data.answer_md).trim());
  const last = corrections[corrections.length - 1];
  if (!last) return { correctId: null, correctIds: [], ids: [], multiple: false };
  const distractors = blocks
    .filter(
      (b) => b.section === 'correction' && b.block_type === 'distractor' && b.parent_id === last.id,
    )
    .sort(byPosition);
  const alsoCorrect = distractors.filter((d) => d.data.is_correct === true).map((d) => d.id);
  return {
    correctId: last.id,
    correctIds: [last.id, ...alsoCorrect],
    ids: [last.id, ...distractors.map((d) => d.id)],
    multiple: last.data.answer_mode === 'multiple' || alsoCorrect.length > 0,
  };
}

/**
 * Pourquoi une question n'a pas de feuille possible (sans appel à l'IA), ou null.
 * Un QCM à plusieurs bonnes réponses a sa feuille depuis le lot 6.6 : ses bonnes
 * réponses sont des écritures d'une même réponse (« 3/5 » et « 0,6 »), la porte du
 * moteur vérifie chacune.
 */
export function sheetBlocker(qcm: QuestionQcm): string | null {
  if (!qcm.correctId) return 'La question n’a pas encore de réponse (corrigé QCM).';
  return null;
}

/**
 * Les choix du modèle doivent être CEUX du QCM (mêmes id), avec les bonnes réponses
 * au bon endroit : c'est ce qui permet au player de marquer « ✓ Dans ton brouillon ».
 */
export function checkChoices(model: unknown, qcm: QuestionQcm): ScratchIssue[] {
  const choices = (model as { choices?: unknown } | null)?.choices;
  if (!Array.isArray(choices) || !choices.length)
    return [{ code: 'choices', message: 'Le modèle ne reprend pas les choix du QCM.' }];
  const issues: ScratchIssue[] = [];
  const known = new Set(qcm.ids);
  const listed = choices.map((c) => c as { id?: unknown; correct?: unknown; label?: unknown });
  listed.forEach((c) => {
    if (typeof c.id !== 'string' || !known.has(c.id))
      issues.push({
        code: 'choices',
        message: `Choix inconnu du QCM (id ${String(c.id)}) : reprends les id fournis.`,
      });
  });
  const correct = listed.filter((c) => c.correct === true);
  const expected = qcm.correctIds?.length ? qcm.correctIds : qcm.correctId ? [qcm.correctId] : [];
  const sameSet =
    correct.length === expected.length && correct.every((c) => expected.includes(String(c.id)));
  if (!sameSet)
    issues.push({
      code: 'choices',
      message:
        expected.length > 1
          ? `Les bonnes réponses du modèle ne sont pas celles du QCM (${expected.length} choix corrects : la correction et les distracteurs marqués justes).`
          : 'La bonne réponse du modèle n’est pas celle du QCM (un seul choix correct, l’id de la correction).',
    });
  // Réponse en couple ou complexe (« (5 ; −5) », « 3 − i ») : une seule valeur
  // numérique ne la vérifie pas (la feuille trouverait 3 pour « 3 − i »).
  const pair = /;|(^|[^a-zA-Z])i([^a-zA-Z]|$)/;
  // congruences : la valeur est LE reste, le libellé peut être une phrase (« Oui ; x ≡ 11 [13] »)
  const congruence = !!(model as { congruence?: unknown } | null)?.congruence;
  const coupled = (correct as { label?: unknown; value?: unknown }[]).some(
    (right) =>
      !congruence &&
      typeof right.label === 'string' &&
      pair.test(right.label) &&
      right.value !== undefined,
  );
  if (coupled)
    issues.push({
      code: 'choices',
      message:
        'La bonne réponse est un couple ou un complexe : chaque composante est une grandeur du but, et les choix n’ont pas de value.',
    });
  const missing = qcm.ids.filter((id) => !listed.some((c) => c.id === id));
  if (missing.length)
    issues.push({
      code: 'choices',
      message: `${missing.length} choix du QCM manquent dans le modèle : reprends-les tous.`,
    });
  return issues;
}

/**
 * Noms de grandeurs que l'élève ne peut pas lire : une probabilité codée
 * (« PnotAB », « pAbB », « PU₁ ») au lieu de « P(A) », « P_A(B) ».
 * Le moteur v2 lit la notation fonctionnelle : on l'exige.
 */
// « Prouge » n'est pas refusé : il ne se distingue pas d'un « Pmax » légitime.
const CODED_PROBABILITY = /^[pP](not|non|bar)?[A-Z][\u0304A-Za-z₀-₉]*$|^[pP](not|non)[A-Za-z₀-₉]+$/;
export function checkNames(model: unknown): ScratchIssue[] {
  const symbols = (model as { symbols?: Record<string, unknown> } | null)?.symbols;
  if (!symbols || typeof symbols !== 'object') return [];
  const coded = Object.keys(symbols).filter((n) => !n.includes('(') && CODED_PROBABILITY.test(n));
  return coded.length
    ? [
        {
          code: 'names',
          message: `Noms illisibles pour l’élève (${coded.slice(0, 4).join(', ')}) : écris les probabilités « P(A) », « P(Ā) », « P_A(B) », « P(A∩B) » avec les événements de l’énoncé.`,
        },
      ]
    : [];
}

/** Ce qu'on enregistre dans `scratch_models` pour une question. */
export interface SheetRowInput {
  model: unknown;
  status: 'draft' | 'ineligible';
  ineligible_reason: string | null;
  validation: Record<string, unknown>;
  max_hora: number;
  engine_version: number;
}

export interface GateResult {
  ok: boolean;
  issues: ScratchIssue[];
  row: SheetRowInput;
}

// ── nouvelles feuilles : nouveau moteur seulement (lot 6.0) ────────────────

/** Version d'une feuille (migration 0532) : ancien moteur ou noyau. */
export type SheetTrack = 'legacy' | 'kernel';

/** Version d'un modèle : le noyau a la clé `kernel`. */
export function modelTrack(model: unknown): SheetTrack {
  return model && typeof model === 'object' && 'kernel' in model ? 'kernel' : 'legacy';
}

/**
 * Décision du 15/10/2026 : les NOUVELLES feuilles ne sortent qu'au nouveau
 * moteur. Une proposition de l'IA sans clé `kernel` relève de l'ancien moteur :
 * la question reste sans feuille (pas de relance), rattrapée quand le noyau
 * saura la faire.
 */
export const LEGACY_ENGINE_REASON =
  'Relève de l’ancien moteur (formules à appliquer, unités, congruences…) : le nouveau moteur ne sait pas encore faire cette question.';

/** La ligne « hors champ » d'une proposition de l'ancien moteur. */
export function legacyEngineRow(engineVersion: number): SheetRowInput {
  return {
    model: {},
    status: 'ineligible',
    ineligible_reason: LEGACY_ENGINE_REASON,
    validation: { ok: false, category: 'legacy_engine' },
    max_hora: 0,
    engine_version: engineVersion,
  };
}

/** Porte de validation du moteur + choix du QCM. Plafond = HORA du corrigé × 1,25 (comme le pilote). */
export function gateDraft(model: unknown, qcm: QuestionQcm, notes?: string): GateResult {
  const report = validateModel(model);
  const issues: ScratchIssue[] = [
    ...report.issues,
    ...checkChoices(model, qcm),
    ...checkNames(model),
  ];
  const ok = report.ok && issues.length === 0;
  const validation: Record<string, unknown> = {
    ok,
    engine_version: report.engine_version,
    issues,
    reached: report.reached,
    max_hora: report.max_hora,
    max_judge_ms: Math.round(report.max_judge_ms),
    distractors_as_traps: report.distractors_as_traps,
    solution: report.solution,
    ...(report.direct_tool ? { direct_tool: report.direct_tool } : {}),
    ...(notes ? { notes } : {}),
  };
  const maxHora = report.max_hora > 0 ? Math.min(200, Math.ceil(report.max_hora * 1.25)) : 0;
  return {
    ok,
    issues,
    row: {
      model: model && typeof model === 'object' ? model : {},
      status: 'draft',
      ineligible_reason: null,
      validation,
      max_hora: maxHora,
      // version MINIMALE du moteur pour jouer cette feuille (2, 3, 4 ou 5) : une app
      // installée d'une version antérieure la joue encore si elle n'en utilise rien de neuf
      engine_version:
        model && typeof model === 'object'
          ? modelEngineVersion(model as Parameters<typeof modelEngineVersion>[0])
          : report.engine_version,
    },
  };
}
